import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  color: #e0e6ed;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  margin-bottom: 10px;
  color: #667eea;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding-left: 20px;
  
  li {
    margin-bottom: 10px;
    position: relative;
    
    &:before {
      content: '•';
      color: #667eea;
      font-size: 1.2em;
      position: absolute;
      left: -20px;
    }
  }
`;

const HowItWorks = () => {
  return (
    <Container>
      <Section>
        <p>
          The Cloud Security Configuration Checker (v3.0, 2026 Edition) is a comprehensive DIY assessment tool that helps you evaluate your cloud security posture across multiple providers and compliance frameworks — including DORA, NIS2, and PCI DSS v4.0. No cloud credentials required.
        </p>
      </Section>

      <Section>
        <SectionTitle>
          <span>☁️</span>Cloud Providers
        </SectionTitle>
        <p>
          This tool supports multiple cloud service providers, each with specialized security checks:
        </p>
        <FeatureList>
          <li><strong>AWS</strong> - Amazon Web Services security configurations and best practices</li>
          <li><strong>Azure</strong> - Microsoft Azure platform security controls</li>
          <li><strong>Google Cloud</strong> - Google Cloud Platform security settings</li>
          <li><strong>Alibaba Cloud</strong> - Alibaba Cloud security features</li>
          <li><strong>IBM Cloud</strong> - IBM Cloud security configurations</li>
          <li><strong>Oracle Cloud</strong> - Oracle Cloud Infrastructure security</li>
          <li><strong>Multi-Cloud</strong> - Cross-cloud security considerations</li>
        </FeatureList>
      </Section>

      <Section>
        <SectionTitle>
          <span>🔒</span>Security Assessment Categories
        </SectionTitle>
        <p>
          Each provider assessment includes specialized categories such as:
        </p>
        <FeatureList>
          <li><strong>Identity & Access Management</strong> - User authentication, permissions, and privilege management</li>
          <li><strong>Network Security</strong> - Network isolation, traffic protection, and perimeter defense</li>
          <li><strong>Data Protection</strong> - Encryption, access controls, and data sovereignty</li>
          <li><strong>Monitoring & Compliance</strong> - Logging, detection, and compliance validation</li>
          <li><strong>API Security</strong> - API access controls and protection mechanisms</li>
          <li><strong>Container Security</strong> - Protection for containerized applications</li>
          <li><strong>Serverless Security</strong> - Security controls for serverless functions</li>
          <li><strong>Advanced Security Features</strong> - Provider-specific security capabilities</li>
        </FeatureList>
      </Section>

      <Section>
        <SectionTitle>
          <span>📋</span>Compliance Framework Mappings
        </SectionTitle>
        <p>
          Security checks are mapped to specific controls from major compliance frameworks:
        </p>
        <FeatureList>
          <li><strong>CSA CCM v4</strong> - Cloud Security Alliance Cloud Controls Matrix</li>
          <li><strong>ISO 27001:2022 / ISO 27017</strong> - International information security standards</li>
          <li><strong>NIST CSF 2.0</strong> - NIST Cybersecurity Framework (2024 update)</li>
          <li><strong>CIS Controls v8</strong> - Center for Internet Security Critical Security Controls</li>
          <li><strong>SOC 2 Type II</strong> - AICPA Trust Services Criteria</li>
          <li><strong>PCI DSS v4.0</strong> - Payment Card Industry Data Security Standard</li>
          <li><strong>DORA</strong> - EU Digital Operational Resilience Act (effective Jan 2025)</li>
          <li><strong>NIS2 Directive</strong> - EU Network and Information Security Directive</li>
        </FeatureList>
        <p>
          You can filter checks by specific frameworks to focus on the compliance requirements most relevant to your organization.
        </p>
      </Section>

      <Section>
        <SectionTitle>
          <span>📊</span>Assessment Results
        </SectionTitle>
        <p>
          After completing the assessment, you'll receive:
        </p>
        <FeatureList>
          <li>An overall security posture score</li>
          <li>Prioritized recommendations for improving security</li>
          <li>A detailed downloadable report including compliance mappings</li>
        </FeatureList>
      </Section>

      <Section>
        <p>
          <em>Note: This tool provides guidance based on security best practices. Always consult with security professionals for your specific requirements.</em>
        </p>
      </Section>
    </Container>
  );
};

export default HowItWorks; 